import React from "react"
import { Modal, Col, Button, Row, Container, Spinner } from "react-bootstrap"
import { Subject, concat, of, Subscription } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import ActionButton from "../../ActionButton"
import { APIError } from "../../../api/ObservableFromFetch"
import { Permission, PendingPermissionsGet, InactivePermissionsGet, PermissionsApprove } from "../../../api/Administration/Permissions"
import DualTableBox from "../DualTableBox"
import Checkbox from "../../Checkbox"

interface ApproveNewPermissionsModalProps {
  onCancel: () => void
  userName?: string
}

interface ApproveNewPermissionsModalState {
  loading: boolean
  saving: boolean
  showInactive: boolean
  permissions: Permission[]
  selectedPermissions: Permission[]
}

export default class ApproveNewPermissionsModal extends React.PureComponent<ApproveNewPermissionsModalProps, ApproveNewPermissionsModalState> {
  state: ApproveNewPermissionsModalState = {
    loading: false,
    saving: false,
    showInactive: false,
    permissions: [],
    selectedPermissions: []
  }

  showInactivePermissions$ = new Subject<boolean>()
  loadSubscription?: Subscription
  saveSubscription?: Subscription

  componentDidMount() {
    this.loadSubscription = this.showInactivePermissions$.pipe(
      switchMap(showInactive => concat(
        of({ loading: true, showInactive: showInactive }),
        (showInactive
          ? InactivePermissionsGet
          : PendingPermissionsGet
        )().pipe(
          map(result => ({ loading: false, result: result })),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, showInactive?: boolean, result?: Permission[] }) => this.setState(prevState => ({
        loading: o.loading,
        showInactive: o.showInactive === undefined ? prevState.showInactive : o.showInactive,
        permissions: o.result || prevState.permissions,
        selectedPermissions: []
      })),
      (error) => this.handleAPIError(error, { loading: false })
    )
    this.showInactivePermissions$.next(this.state.showInactive)
  }

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: ApproveNewPermissionsModalProps, prevState: ApproveNewPermissionsModalState) {
    if (prevState.saving && !this.state.saving) {
      this.showInactivePermissions$.next(this.state.showInactive)
    }
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleApprove = () => {
    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = PermissionsApprove(this.state.selectedPermissions, this.props.userName || "").subscribe(
      data => {
        this.setState({ saving: false, permissions: [], selectedPermissions: [] })
        toast.success(data)
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  render() {
    const { loading, saving, showInactive, permissions, selectedPermissions } = this.state

    return (<>
      <Modal.Dialog size="xl">
        <Modal.Header>
          <Modal.Title className="text-uppercase">APPROVE PERMISSIONS {loading && <Spinner animation="border" />}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0">
          <DualTableBox idKey="PermissionID"
            leftTitle="Pending Approval"
            rightTitle="To Be Approved"
            leftColumns={[{ title: "PERMISSION", value: "Title" }, { title: "STATUS", value: "RecordStatusType" }]}
            rightColumns={[{ title: "PERMISSION", value: "Title" }, { title: "STATUS", value: "RecordStatusType" }]}
            options={permissions}
            optionsAtRight={selectedPermissions}
            onChange={(options: Permission[]) => this.setState({ selectedPermissions: options })} />
          <Container fluid>
            <Row>
              <Col>
                <Checkbox id="showInactive"
                  label="Reactivate Permission"
                  checked={showInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.showInactivePermissions$.next(e.target.checked)} />
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="mr-2" onClick={this.props.onCancel}>Cancel</Button>
          <ActionButton variant="primary" onClick={this.handleApprove} disabled={loading || saving || selectedPermissions.length === 0} spin={saving} disableOnSpin >Approve</ActionButton>
        </Modal.Footer>
      </Modal.Dialog>
    </>)
  }
}
